import { accosBarDataToGuestDataStoreJsonString, setCookie } from '@/composables/cookie'
import { broadcastChannelMessages } from '@/composables/helpers'
import type { IAccosBarDto } from '@/composables/interfaces/apiInterfaces'
import type { ILocation, IOccupancyLocalStorage, IRoom } from '@/composables/interfaces/appInterfaces'
import { GdsCookie, OccupancyLocalStorage, PetsAllowedAmenityId } from '@/constants'
import { watchIgnorable } from '@vueuse/core'
import { ref, watch } from 'vue'
import { filtersData, filterStore } from './filter.store'

export const location = ref<ILocation | null>(null)
export const date = ref<(Date | null)[] | null>(null)
export const rooms = ref<Array<IRoom>>([{ adults: 2, children: 0, childrenAges: [] }])
export const pets = ref<{ hasPets: boolean; message: string }>({ hasPets: false, message: '' })

/**
 * variable which stores full area table in current language
 */
export const areas = ref<IAccosBarDto[] | null>([])

/**
 * ref value which stores every single data of accosbar
 *
 * Note: ref value changes if one of the values in the object change.
 */
export const accosBarData = ref({ location, date, rooms, pets })

export const popupVisible = ref(false)

export const broadcasterAccosBarStore = new BroadcastChannel(OccupancyLocalStorage)
export const messageReceiverAccosBarStore = new BroadcastChannel(OccupancyLocalStorage)

export function initAccosBarStoreFromLocalStorage(): void {
  const localStorageData = localStorage.getItem(OccupancyLocalStorage)
  if (localStorageData != null) {
    const storedData: IOccupancyLocalStorage = JSON.parse(localStorageData!)
    //ignoreUpdates otherwise endless loop if two apps are on the same page
    watchAccosBarData.ignoreUpdates(() => {
      rooms.value = storedData.rooms
      date.value = storedData.date ? storedData.date.map((d: string | null) => (d != null ? new Date(d) : null)) : null
      location.value = storedData.location
      pets.value = storedData.pets
    })
  }
}

watch(location, (newLocation) => {
  filterStore.value.areaId = newLocation?.areaId ?? 0
})

watch(
  //done to keep pets.value.hasPets and filterStore.value.amenityIds in sync
  () => pets.value.hasPets,
  () => {
    //checking if PetAmenityId is a active amenityid
    if (filtersData.value?.amenities.map((x) => x.id).includes(PetsAllowedAmenityId)) {
      if (pets.value.hasPets) {
        if (!filterStore.value.amenityIds.includes(PetsAllowedAmenityId)) {
          filterStore.value.amenityIds.push(PetsAllowedAmenityId)
        }
      } else {
        if (filterStore.value.amenityIds.includes(PetsAllowedAmenityId)) {
          const x = filterStore.value.amenityIds.findIndex((x) => x == PetsAllowedAmenityId)
          filterStore.value.amenityIds.splice(x)
        }
      }
    }
  }
)

messageReceiverAccosBarStore.addEventListener('message', (e) => {
  switch (e.data) {
    case broadcastChannelMessages.UPDATE:
      initAccosBarStoreFromLocalStorage()
      break
    default:
      console.warn(`NO HANDLER FOR MESSAGE: ${e.data}`)
  }
})

function emitToOtherApps(): void {
  broadcasterAccosBarStore.postMessage(broadcastChannelMessages.UPDATE)
}

const watchAccosBarData = watchIgnorable(
  accosBarData.value,
  () => {
    setOccupancyLocalStorageAndCookie()
    emitToOtherApps()
  },
  { deep: true }
)

function setOccupancyLocalStorageAndCookie(): void {
  localStorage.setItem(OccupancyLocalStorage, JSON.stringify(accosBarData.value))
  setCookie(GdsCookie, accosBarDataToGuestDataStoreJsonString(), 365)
}
